import { convexQuery, useConvexMutation } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef, SortingState } from '@tanstack/react-table'
import { useTable } from '@tanstack/react-table'
import { useConvex } from 'convex/react'
import type { FunctionReturnType } from 'convex/server'
import { IconCubes2Outline18, IconTrashOutline18 } from 'nucleo-ui-outline-18'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '~/components/ui/button'
import type { DataTableFeatures } from '~/components/ui/data-table'
import {
  DataTable,
  dataTableFeatures,
  dataTableSortOptions,
  makeGlobalFilter,
} from '~/components/ui/data-table'
import { EmptyState } from '~/components/ui/empty-state'
import { PageHeader } from '~/components/ui/page-header'
import { SearchField } from '~/components/ui/search-field'
import { confirm } from '~/lib/confirm'
import { titleFor } from '~/lib/route-title'
import { api } from '~convex/_generated/api'
import type { Id } from '~convex/_generated/dataModel'

export const Route = createFileRoute('/admin/catalog/brands')({
  component: BrandsRoute,
  head: () => ({ meta: [{ title: titleFor(msg`Catalog brands`) }] }),
})

/**
 * Derived rather than restated: this row was hand-mirrored and had already
 * drifted from what the query returns. See CLAUDE.md → Type safety.
 */
type BrandRow = FunctionReturnType<typeof api.catalogAdminQueries.listBrands>[number]

const EMPTY: BrandRow[] = []

/**
 * TAKEDOWN — the only control left after the Casoca agreement replaced the
 * per-brand licence gate, and until now the only way to use it was a terminal.
 *
 * Two levers, deliberately different in kind:
 *
 * - HIDE is one write on the brand and it is REVERSIBLE. Its products drop out
 *   of every tenant read on the next query and ingest refuses new observations
 *   for it, so a crawl cannot undo it. This is what answers an objection.
 * - PURGE deletes the brand's imagery and files from the catalog bucket. It is
 *   NOT reversible, and it is gated on the brand already being hidden — doing
 *   it to a live brand would blank its products while leaving them listed.
 *
 * The usage numbers are counts only and never say which workspace. That is the
 * whole shape of what a brand may one day be shown.
 */
function BrandsRoute() {
  const { t, i18n } = useLingui()
  const convex = useConvex()
  const { data: brands, isPending } = useQuery(convexQuery(api.catalogAdminQueries.listBrands, {}))
  const { mutateAsync: setBrandStatus, isPending: updating } = useMutation({
    mutationFn: useConvexMutation(api.catalogAdmin.setBrandStatus),
  })
  const [purging, setPurging] = useState<Id<'catalogBrands'> | null>(null)
  const [sorting, setSorting] = useState<SortingState>([{ desc: false, id: 'name' }])
  const [globalFilter, setGlobalFilter] = useState('')

  const hidden = (brands ?? []).filter((brand) => brand.status === 'hidden')

  const toggle = async (brand: BrandRow) => {
    const hiding = brand.status === 'active'
    const confirmed = await confirm({
      confirmLabel: hiding ? t`Hide brand` : t`Show brand`,
      description: hiding
        ? t`${brand.productCount} products drop out of every tenant read immediately, and the ingest endpoint starts refusing observations for this brand. Library rows that already adopted one keep their name, price and image. Reversible.`
        : t`This brand's products become visible again everywhere.`,
      destructive: hiding,
      title: hiding ? t`Hide ${brand.name}?` : t`Show ${brand.name} again?`,
    })
    if (!confirmed) {
      return
    }
    try {
      await setBrandStatus({ brandId: brand._id, status: hiding ? 'hidden' : 'active' })
      toast.success(hiding ? t`${brand.name} is hidden.` : t`${brand.name} is visible again.`)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t`Could not change the brand.`)
    }
  }

  const purge = async (brand: BrandRow) => {
    const confirmed = await confirm({
      confirmLabel: t`Purge media`,
      description: t`Deletes every image and file under catalog/${brand.slug}/ from the catalog bucket. This cannot be undone, and the brand's products will render without images afterwards. Safe to run twice.`,
      destructive: true,
      title: t`Purge ${brand.name}'s media?`,
    })
    if (!confirmed) {
      return
    }
    setPurging(brand._id)
    try {
      const result = await convex.action(api.catalogAdminNode.purgeBrandMedia, {
        brandId: brand._id,
      })
      toast.success(
        result.done
          ? t`Purged ${result.deleted} objects.`
          : t`Purged ${result.deleted} objects. Run it again to finish.`
      )
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t`Could not purge the media.`)
    } finally {
      setPurging(null)
    }
  }

  const columns: ColumnDef<DataTableFeatures, BrandRow>[] = [
    {
      accessorKey: 'name',
      cell: ({ row }) => (
        <span className="flex items-center gap-2.5">
          {row.original.logoUrl === null ? (
            <span className="flex size-8 shrink-0 items-center justify-center rounded-md bg-muted font-medium text-muted-foreground text-xs">
              {row.original.name.slice(0, 1).toUpperCase()}
            </span>
          ) : (
            <img
              alt=""
              className="size-8 shrink-0 rounded-md bg-muted object-contain"
              height={32}
              src={row.original.logoUrl}
              width={32}
            />
          )}
          <span className="min-w-0">
            <span className="block truncate">{row.original.name}</span>
            <span className="block truncate font-normal text-muted-foreground text-xs">
              {row.original.sourceDomains.join(', ')}
            </span>
          </span>
          {row.original.status === 'hidden' && (
            <span className="shrink-0 rounded-full bg-destructive/10 px-2 py-0.5 font-medium text-destructive text-xs">
              <Trans>hidden</Trans>
            </span>
          )}
        </span>
      ),
      header: () => t`Brand`,
      meta: { className: 'font-medium' },
    },
    {
      accessorKey: 'productCount',
      cell: ({ row }) => i18n.number(row.original.productCount),
      header: () => t`Products`,
      meta: { className: 'text-right text-muted-foreground tabular-nums' },
    },
    {
      accessorKey: 'views',
      cell: ({ row }) => i18n.number(row.original.views),
      header: () => t`Views`,
      meta: { className: 'text-right text-muted-foreground tabular-nums' },
    },
    {
      accessorKey: 'adoptions',
      cell: ({ row }) => i18n.number(row.original.adoptions),
      header: () => t`Adoptions`,
      meta: { className: 'text-right text-muted-foreground tabular-nums' },
    },
    {
      accessorKey: 'downloads',
      cell: ({ row }) => i18n.number(row.original.downloads),
      header: () => t`Downloads`,
      meta: { className: 'text-right text-muted-foreground tabular-nums' },
    },
    {
      cell: ({ row }) => (
        <span className="flex items-center justify-end gap-1.5">
          <Button
            // Showing a brand mid-purge is refused by the server; disabling
            // it here is how the admin finds that out before clicking.
            disabled={updating || (row.original.status === 'hidden' && row.original.purging)}
            onClick={(event) => {
              event.stopPropagation()
              void toggle(row.original)
            }}
            size="sm"
            title={row.original.purging ? t`Purging this brand's media…` : undefined}
            type="button"
            variant="outline"
          >
            {row.original.status === 'active' ? <Trans>Hide</Trans> : <Trans>Show</Trans>}
          </Button>
          {row.original.status === 'hidden' && (
            <Button
              aria-label={t`Purge media`}
              disabled={purging !== null}
              onClick={(event) => {
                event.stopPropagation()
                void purge(row.original)
              }}
              size="icon-sm"
              type="button"
              variant="ghost"
            >
              <IconTrashOutline18 />
            </Button>
          )}
        </span>
      ),
      enableSorting: false,
      header: () => '',
      id: 'actions',
      meta: { className: 'text-right' },
    },
  ]

  const globalFilterFn = useMemo(
    () => makeGlobalFilter<BrandRow>((brand) => [brand.name, brand.slug, ...brand.sourceDomains]),
    []
  )

  const table = useTable({
    ...dataTableSortOptions,
    columns,
    data: brands ?? EMPTY,
    features: dataTableFeatures,
    globalFilterFn,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    state: { globalFilter, sorting },
  })

  return (
    <div className="flex h-full flex-col">
      <PageHeader count={brands?.length} title={t`Catalog brands`}>
        <SearchField onChange={setGlobalFilter} value={globalFilter} />
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-3 pb-4">
        {hidden.length > 0 && (
          <div className="mb-3 rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm">
            <Trans>
              {hidden.length} brands are hidden: {hidden.map((brand) => brand.name).join(', ')}.
              Their products are absent from every tenant read.
            </Trans>
          </div>
        )}
        <DataTable
          empty={
            <EmptyState
              description={t`Brands arrive through the import endpoint; nothing has been posted yet.`}
              icon={IconCubes2Outline18}
              title={t`No brands yet`}
            />
          }
          isPending={isPending}
          table={table}
        />
      </div>
    </div>
  )
}
